import { useCallback, useEffect, useRef } from "react";
import { useSocketContext } from "../context/SocketContext";

const MARK_READ_DEBOUNCE_MS = 400;

function parseTeamMessagePayload(payload) {
  if (typeof payload === "string") {
    try {
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }
  return payload && typeof payload === "object" ? payload : null;
}

function isTeamMetaUpdate(data) {
  return Boolean(data?.syncOnly || data?.reactionUpdate || data?.receiptUpdate);
}

/**
 * Escuta team_message no socket e sincroniza mensagens do espaço aberto.
 * Marca como lido com debounce enquanto a conversa estiver aberta e a aba visível
 * (padrão Slack/WhatsApp: leitura automática ao visualizar o thread).
 */
export const useRealtimeTeamMessages = ({
  selectedSpaceId,
  onSpaceActivity,
  syncLatestMessages,
  refetchSpaces,
  markRead,
  markReadEnabled = true,
}) => {
  const { socket } = useSocketContext();
  const markReadTimerRef = useRef(null);
  const markReadRef = useRef(markRead);
  const refetchSpacesRef = useRef(refetchSpaces);

  useEffect(() => {
    markReadRef.current = markRead;
  }, [markRead]);

  useEffect(() => {
    refetchSpacesRef.current = refetchSpaces;
  }, [refetchSpaces]);

  const flushMarkRead = useCallback((spaceId) => {
    const sid = Number(spaceId);
    if (!Number.isFinite(sid) || sid <= 0) return;
    const mark = markReadRef.current;
    if (!mark) return;

    mark(sid)
      .catch(() => {})
      .finally(() => {
        refetchSpacesRef.current?.();
      });
  }, []);

  const scheduleMarkRead = useCallback(
    (spaceId) => {
      if (!markReadEnabled || !markReadRef.current) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") {
        return;
      }

      const sid = Number(spaceId);
      if (!Number.isFinite(sid) || sid <= 0) return;

      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
      }
      markReadTimerRef.current = window.setTimeout(() => {
        markReadTimerRef.current = null;
        flushMarkRead(sid);
      }, MARK_READ_DEBOUNCE_MS);
    },
    [markReadEnabled, flushMarkRead]
  );

  useEffect(() => {
    return () => {
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedSpaceId || !markReadEnabled || !markReadRef.current) return undefined;

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleMarkRead(selectedSpaceId);
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [selectedSpaceId, markReadEnabled, scheduleMarkRead]);

  useEffect(() => {
    if (!socket) return;

    const handleTeamMessage = (payload) => {
      const data = parseTeamMessagePayload(payload);
      if (!data) return;

      const spaceId = data?.spaceId != null ? Number(data.spaceId) : null;
      if (!spaceId) return;

      const metaUpdate = isTeamMetaUpdate(data);
      const isOpenSpace = selectedSpaceId && Number(selectedSpaceId) === spaceId;

      if (isOpenSpace) {
        if (syncLatestMessages) {
          syncLatestMessages(metaUpdate ? { refreshRecent: true } : undefined);
        }
        if (!metaUpdate) {
          scheduleMarkRead(spaceId);
        }
      } else if (refetchSpacesRef.current) {
        refetchSpacesRef.current(spaceId);
      }

      if (onSpaceActivity) {
        onSpaceActivity({ eventName: "team_message", spaceId, messageId: data?.messageId });
      }
    };

    socket.on("team_message", handleTeamMessage);
    return () => {
      socket.off("team_message", handleTeamMessage);
    };
  }, [socket, selectedSpaceId, onSpaceActivity, syncLatestMessages, scheduleMarkRead]);
};
